import {
  mountSchoolChat,
  type SchoolChatHandle,
} from "@axl/site-chat/src/school/mountSchoolChat";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { Navigate } from "react-router-dom";

import { appSettings } from "@app/shared/lib/app-settings";
import { useSdk, useTokenStorage, useUser } from "@app/shared/lib/hooks";

/**
 * `pending` — asking AccelChat whether the school has a membership chat; `site` — it has: the website chat
 * (`@axl/site-chat` in its School mode); `none` — it has not, the student has no chat.
 */
type ChatKind = "pending" | "site" | "none";

/** What the header button and the "technical support" links need from the chat. */
export type ChatController = {
  open: () => void;
  close: () => void;
  readonly visible: boolean;
};

type ChatContextType = {
  chatController: ChatController | null;
  isChatInited: boolean;
  unreadCount: number;
  /** The launcher image set in the website chat's settings; the header button shows it instead of the glyph. */
  icon: string | null;
  kind: ChatKind;
  /**
   * Opens the chat of student `userId` with the cursor in the composer — now, or as soon as that student's chat is up
   * (a `/support` link right after signing in). Nothing happens when the school has no membership chat.
   */
  openWithCursor: (userId: string) => void;
};

const ChatContext = createContext<ChatContextType>({} as ChatContextType);

/** AccelChat's root: School's chat API setting ends with `/api`, the website chat adds its own paths. */
const chatRoot = () =>
  String(appSettings.chat.apiUrl ?? "").replace(/\/api\/?$/, "");

const asController = (chat: SchoolChatHandle): ChatController => ({
  open: () => chat.open(),
  close: () => chat.close(),
  get visible() {
    return chat.isOpen;
  },
});

export const ChatProvider: FC<PropsWithChildren> = ({ children }) => {
  const sdk = useSdk();
  const tokenStorage = useTokenStorage();
  const tokenRef = useRef<string | undefined>(undefined);
  tokenRef.current = tokenStorage.accessToken;
  // The chat belongs to the signed-in student, not to "some token": switching accounts (login-as, a login link,
  // signing in again) replaces the token without it ever going empty, and a chat keyed on "signed in" kept the
  // previous student's session and thread.
  const { user } = useUser();
  const userId = tokenStorage.accessToken ? user?.id : undefined;

  const [kind, setKind] = useState<ChatKind>("pending");
  const [controller, setController] = useState<ChatController | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [icon, setIcon] = useState<string | null>(null);
  // the chat that is up and whose student it is; a `/support` link for a student whose chat is not up yet waits here
  const readyChatRef = useRef<{ userId: string; chat: SchoolChatHandle } | null>(null);
  const pendingOpenRef = useRef<string | null>(null);

  const openWithCursor = (forUserId: string) => {
    if (readyChatRef.current?.userId === forUserId) {
      readyChatRef.current.chat.open({ focus: true });
      return;
    }
    pendingOpenRef.current = forUserId;
  };

  useEffect(() => {
    setKind("pending");
    setUnreadCount(0);
    if (!userId) return;
    const chat = mountSchoolChat({
      apiUrl: chatRoot(),
      // The SDK's own cache first: it holds the new student's token before their profile arrives, while the
      // polled `tokenStorage` may still be a second behind.
      getToken: () => sdk.tokenStorage.tokens?.accessToken ?? tokenRef.current,
    });
    let alive = true;
    const unsubscribe = chat.subscribe(() => setUnreadCount(chat.unread));
    chat.ready
      .then((ok) => {
        if (!alive) return;
        if (!ok) {
          // a chat link to a school without a membership chat just opens the school
          if (pendingOpenRef.current === userId) pendingOpenRef.current = null;
          setKind("none");
          return;
        }
        readyChatRef.current = { userId, chat };
        setController(asController(chat));
        setUnreadCount(chat.unread);
        setIcon(chat.logo);
        setKind("site");
        if (pendingOpenRef.current === userId) {
          pendingOpenRef.current = null;
          chat.open({ focus: true });
        }
      })
      .catch(() => alive && setKind("none"));
    return () => {
      alive = false;
      if (readyChatRef.current?.chat === chat) readyChatRef.current = null;
      unsubscribe();
      chat.dispose();
      setController(null);
      setIcon(null);
    };
  }, [userId, sdk]);

  const ctxValue: ChatContextType = {
    chatController: kind === "site" ? controller : null,
    isChatInited: kind === "site",
    unreadCount,
    icon: kind === "site" ? icon : null,
    kind,
    openWithCursor,
  };

  return (
    <ChatContext.Provider value={ctxValue}>{children}</ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);

/** Whether the student has a chat at all: the school's membership chat. */
export const useChatAvailable = () => useChat().kind === "site";

/**
 * `/support` — the chat link (the admin's contact card copies `/c/{contactId}/support`, which the contact page turns into
 * this route after the sign-in). Not a page: the signed-in student lands on the home page with the chat open and the
 * cursor in the composer. Signed out, the main layout sends them to the login form with `returnUrl=/support`, so the
 * link survives the sign-in.
 */
export const ChatSupportRoute: FC = () => {
  const { openWithCursor } = useChat();
  const { user } = useUser();
  const userId = user?.id;

  useEffect(() => {
    if (userId) openWithCursor(userId);
    // once per visit of the link
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  return <Navigate to="/" replace />;
};
